import axios from "axios";
import api from "../api";
import { DecryptService } from "../decrypt";

const DEFAULT_TIMEOUT = 15000;

const inferMimeTypeFromUrl = (url: string): string => {
    const cleanUrl = String(url || "").toLowerCase().split("?")[0].split("#")[0];
    const withoutEnc = cleanUrl.endsWith(".enc") ? cleanUrl.slice(0, -4) : cleanUrl;

    if (withoutEnc.endsWith(".jpg") || withoutEnc.endsWith(".jpeg")) return "image/jpeg";
    if (withoutEnc.endsWith(".png")) return "image/png";
    if (withoutEnc.endsWith(".webp")) return "image/webp";
    if (withoutEnc.endsWith(".gif")) return "image/gif";
    if (withoutEnc.endsWith(".bmp")) return "image/bmp";
    if (withoutEnc.endsWith(".svg")) return "image/svg+xml";

    return "image/jpeg";
};

const normalizeBlobMimeType = (headerMimeType: string | undefined, imageUrl: string): string => {
    const mime = (headerMimeType || "").toLowerCase();
    if (!mime || mime.includes("octet-stream")) {
        return inferMimeTypeFromUrl(imageUrl);
    }
    return mime;
};

const resolveStorageUrl = (imageUrl: string): string => {
    const overrideStorageBaseUrl =
        import.meta.env.VITE_STORAGE_BASE_URL ||
        import.meta.env.VITE_MEDIA_STORAGE_BASE_URL ||
        "";

    if (!overrideStorageBaseUrl) {
        return imageUrl;
    }

    return imageUrl.replace("https://lldvod.xosp.tv", overrideStorageBaseUrl);
};

const imageBlobCache = new Map<string, Blob>()
const imageBlobInFlight = new Map<string, Promise<Blob>>()

export type EncryptedImageOptions = {
    signal?: AbortSignal
}

export function getCachedImageBlob(imageUrl: string): Blob | undefined {
    return imageBlobCache.get(imageUrl)
}

const runWithImageDedup = async (
    imageUrl: string,
    worker: () => Promise<Blob>,
): Promise<Blob> => {
    const cachedBlob = imageBlobCache.get(imageUrl)
    if (cachedBlob) {
        return cachedBlob
    }

    const inFlight = imageBlobInFlight.get(imageUrl)
    if (inFlight) {
        return inFlight
    }

    const decryptPromise = worker()
    imageBlobInFlight.set(imageUrl, decryptPromise)

    try {
        const blob = await decryptPromise
        imageBlobCache.set(imageUrl, blob)
        return blob
    } finally {
        imageBlobInFlight.delete(imageUrl)
    }
}

export async function CDNImage(imageUrl: string, options: EncryptedImageOptions = {}) {
    return runWithImageDedup(imageUrl, async () => {
    try {
        // Prefer impersonation token when present, then fallback to admin token.
        const token =
            sessionStorage.getItem('impersonation_token') ||
            sessionStorage.getItem('admin_token') ||
            localStorage.getItem('impersonation_token') ||
            localStorage.getItem('admin_token');
        
        // Validation du token
        if (!token) {
            console.error('Aucun token d\'authentification trouvé pour CDNImage');
            throw new Error('Token d\'authentification manquant');
        }
        
        // Validation de l'URL
        if (!imageUrl || imageUrl.trim() === '') {
            console.error('URL de l\'image vide ou invalide:', imageUrl);
            throw new Error('URL de l\'image invalide');
        }
        
        const response = await axios.post(api.playphoto(), {url: imageUrl}, {
            responseType: "blob",
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            timeout: DEFAULT_TIMEOUT,
            signal: options.signal,
        });
        
        if (!response.data) {
            throw new Error('Aucune donnée reçue du serveur');
        }
        
        // Validation du blob reçu
        if (!(response.data instanceof Blob)) {
            console.error('La réponse n\'est pas un blob:', typeof response.data);
            throw new Error('Format de réponse invalide du serveur');
        }
        
        // Vérifier si le blob est valide
        if (response.data.size === 0) {
            throw new Error('Image vide reçue du serveur');
        }

        return response.data;
    } catch (error: any) {
        if (axios.isCancel(error)) {
            throw error;
        }

        console.error('Erreur lors du décryptage de l\'image CDN:', error);
        
        // Gérer les erreurs spécifiques de décodage
        if (error.message && error.message.includes('wrong final block length')) {
            console.error('Erreur de décodage détectée, tentative avec URL normale');
            throw new Error('Erreur de décodage de l\'image - format non supporté');
        }
        
        // Gérer les différents types d'erreurs
        if (error.response) {
            const status = error.response.status;
            const message = error.response.data?.message || error.response.data?.error || 'Erreur serveur';
            
            switch (status) {
                case 401:
                    throw new Error('Non autorisé - token invalide ou expiré');
                case 403:
                    throw new Error('Accès refusé - permissions insuffisantes');
                case 404:
                    throw new Error('Image non trouvée');
                case 413:
                    throw new Error('Image trop volumineuse');
                case 415:
                    throw new Error('Format d\'image non supporté');
                case 500:
                    throw new Error(`Erreur serveur interne: ${message}`);
                default:
                    throw new Error(`Erreur HTTP ${status}: ${message}`);
            }
        } else if (error.request) {
            throw new Error('Erreur réseau - impossible de contacter le serveur');
        } else {
            throw new Error(`Erreur de décryptage: ${error.message}`);
        }
    }
    })
}

export async function CDNImageFrontDecrypt(
    imageUrl: string,
    options: EncryptedImageOptions = {},
): Promise<Blob> {
    if (!imageUrl || imageUrl.trim() === "") {
        throw new Error("URL de l'image invalide");
    }

    return runWithImageDedup(imageUrl, async () => {
        const decryptUtils = new DecryptService();
        const finalUrl = resolveStorageUrl(imageUrl);

        const response = await axios.get(finalUrl, {
            responseType: "arraybuffer",
            timeout: DEFAULT_TIMEOUT,
            signal: options.signal,
        });

        const encryptedBytes = new Uint8Array(response.data);
        const decryptedBytes = await decryptUtils.decryptBuffer(encryptedBytes);
        const mimeType = normalizeBlobMimeType(response.headers?.["content-type"], imageUrl);
        const decryptedArrayBuffer = decryptedBytes.buffer.slice(
            decryptedBytes.byteOffset,
            decryptedBytes.byteOffset + decryptedBytes.byteLength,
        ) as ArrayBuffer;

        return new Blob([decryptedArrayBuffer], { type: mimeType });
    })
}

export async function resolveEncryptedImageBlob(
    imageUrl: string,
    options: EncryptedImageOptions = {},
): Promise<Blob> {
    try {
        return await CDNImageFrontDecrypt(imageUrl, options)
    } catch (frontDecryptError) {
        if (axios.isCancel(frontDecryptError)) {
            throw frontDecryptError
        }
        console.warn('Frontend image decrypt failed, fallback to playphoto endpoint:', frontDecryptError)
        return CDNImage(imageUrl, options)
    }
}
